import ErrorMessage from '@components/Common/ErrorMessage';
import { PageHero } from '@components/Common/Panel';
import FormField from '@components/Common/FormField';
import PageStats from '@components/Common/PageStats';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import MouseOutlinedIcon from '@mui/icons-material/MouseOutlined';
import OpenInBrowserOutlinedIcon from '@mui/icons-material/OpenInBrowserOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutlined';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Drawer,
    Grid,
    IconButton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    EmailAuditFilters,
    EmailSendRecordItem,
    fetchAuditRequest,
} from '@store/features/emailAudit/actions';
import type { RootState } from '@store/rootReducer';
import CloseIcon from '@mui/icons-material/Close';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import './index.scss';
import { SEND_USER_TYPE_OPTIONS, emailAudienceLabel } from '@helpers/emailAudience';
import { formatDateTime } from '@helpers/date';
import { DELIVERY_FILTER_OPTIONS, deliveryChip } from '@helpers/emailDelivery';
import DeliveryChip from '@components/BlockedAddresses/DeliveryChip';

const PERIOD_OPTIONS = [
  { label: '7d',  value: 7  },
  { label: '30d', value: 30 },
  { label: '90d', value: 90 },
];


const OPENED_OPTIONS = [
  { value: 'all',   label: 'Any open status' },
  { value: 'true',  label: 'Opened'          },
  { value: 'false', label: 'Not opened'      },
];

const CLICKED_OPTIONS = [
  { value: 'all',   label: 'Any click status' },
  { value: 'true',  label: 'Clicked'          },
  { value: 'false', label: 'Not clicked'      },
];

const PAGE_SIZE = 20;

function fmtDate(iso: string | null | undefined): string {
  return formatDateTime(iso);
}

const EmailAudit: React.FC = () => {
  const dispatch = useDispatch();
  const { data, loading, error } = useSelector((s: RootState) => s.emailAudit);

  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(0, parseInt(searchParams.get('page') ?? '0', 10));

  const [period, setPeriod]           = useState(30);
  const [userType, setUserType]       = useState('');
  const [templateKey, setTemplateKey] = useState('');
  const [hasOpened, setHasOpened]     = useState('all');
  const [hasClicked, setHasClicked]   = useState('all');
  const [deliveryStatus, setDeliveryStatus] = useState('all');
  const [search, setSearch]           = useState('');
  const [selected, setSelected]       = useState<EmailSendRecordItem | null>(null);

  const buildFilters = useCallback((): EmailAuditFilters => ({
    page,
    size: PAGE_SIZE,
    periodDays: period,
    userType,
    templateKey,
    hasOpened,
    hasClicked,
    deliveryStatus,
    search,
  }), [page, period, userType, templateKey, hasOpened, hasClicked, deliveryStatus, search]);

  useEffect(() => {
    dispatch(fetchAuditRequest(buildFilters()));
  }, [dispatch, buildFilters]);

  const setPage = (newPage: number) => {
    // Page 0 is the default, so it is left out of the URL rather than written as `?page=0`: the
    // table reports page 0 as it mounts, and writing it raced the email-campaigns alias redirect
    // (every panel mounts at once) and put a query on a URL that had none.
    if (String(newPage) === (searchParams.get('page') ?? '0')) return;
    setSearchParams(prev => {
      if (newPage === 0) prev.delete('page'); else prev.set('page', String(newPage));
      return prev;
    }, { replace: true });
  };

  const handleFilterChange = () => setPage(0);

  const resetFilters = () => {
    setUserType('');
    setTemplateKey('');
    setHasOpened('all');
    setHasClicked('all');
    setDeliveryStatus('all');
    setSearch('');
    setPage(0);
  };

  const hasActiveFilters = userType || templateKey || hasOpened !== 'all' || hasClicked !== 'all' || deliveryStatus !== 'all' || search;

  const selectors = ['userEmail', 'userType', 'templateKey', 'sentAt', 'opened', 'clicked', 'deliveryStatus'];

  const tableHeadRow = {
    userEmail:   'Recipient',
    userType:    'User Type',
    templateKey: 'Template',
    sentAt:      'Sent At',
    opened:      'Opened',
    clicked:     'Clicked',
    deliveryStatus: 'Delivery',
  };

  const tableBodyRow = {
    userEmail: (r: EmailSendRecordItem) => (
      <Box className="ea-recipient-cell">
        <Typography className="ea-recipient-email">{r.userEmail || '-'}</Typography>
        <Typography className="ea-recipient-id" variant="caption">{r.userId || ''}</Typography>
      </Box>
    ),
    userType: (r: EmailSendRecordItem) => (
      <Chip
        label={emailAudienceLabel(r.userType)}
        size="small"
        variant="outlined"
        color={(r.userType ?? '').toUpperCase() === 'EMPLOYER' ? 'secondary' : 'default'}
      />
    ),
    templateKey: (r: EmailSendRecordItem) => (
      <Box className="ea-template-cell">
        <Typography className="ea-template-key">{r.templateKey || '-'}</Typography>
        {r.subject && (
          <Typography className="ea-subject" variant="caption">{r.subject}</Typography>
        )}
      </Box>
    ),
    sentAt: (r: EmailSendRecordItem) => (
      <Typography variant="caption">{fmtDate(r.sentAt)}</Typography>
    ),
    opened: (r: EmailSendRecordItem) => r.openCount > 0 ? (
      <Tooltip title={`First: ${fmtDate(r.firstOpenAt)} · Last: ${fmtDate(r.lastOpenAt)}`}>
        <Chip
          size="small"
          icon={<CheckCircleOutlineIcon fontSize="small" />}
          label={r.openCount === 1 ? 'Opened' : `Opened ×${r.openCount}`}
          color="success"
          variant="outlined"
        />
      </Tooltip>
    ) : (
      <Chip
        size="small"
        icon={<RemoveCircleOutlineIcon fontSize="small" />}
        label="Not opened"
        variant="outlined"
      />
    ),
    clicked: (r: EmailSendRecordItem) => r.clickCount > 0 ? (
      <Tooltip title={`First click: ${fmtDate(r.firstClickAt)}`}>
        <Chip
          size="small"
          icon={<MouseOutlinedIcon fontSize="small" />}
          label={r.clickCount === 1 ? 'Clicked' : `Clicked ×${r.clickCount}`}
          color="primary"
          variant="outlined"
        />
      </Tooltip>
    ) : (
      <Chip size="small" label="No clicks" variant="outlined" />
    ),
    /*
     * Only a problem gets a chip. SES reports failures, never deliveries, so a clean row has
     * nothing to say; a dash keeps the column readable and drops off the phone card.
     */
    deliveryStatus: (r: EmailSendRecordItem) => {
      const chip = deliveryChip(r);
      if (!chip) return <Typography variant="caption">-</Typography>;
      return (
        <Tooltip title={r.bounceReason || (chip.label === 'Complained' ? 'Marked as spam by the recipient' : 'No reason given')}>
          <span><DeliveryChip label={chip.label} tone={chip.tone} /></span>
        </Tooltip>
      );
    },
  };

  return (
    <Box className="email-audit-page">
      <Box className="ea-page-body max-w-container">

        {/* ── Hero ────────────────────────────────────────────────────── */}
        <PageHero
          kicker="Email send log"
          title="Every email sent: who received it, when, and whether they opened or clicked."
          text={<>Records from <code>email_send_records</code> · 90-day retention · open + click events tracked</>}
          actions={(
            <ToggleButtonGroup
              value={period}
              exclusive
              size="small"
              onChange={(_, v) => { if (v !== null) { setPeriod(v); setPage(0); } }}
              className="ea-period-toggle"
            >
              {PERIOD_OPTIONS.map((opt) => (
                <ToggleButton key={opt.value} value={opt.value}>{opt.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        />

        {/* ── Filters ─────────────────────────────────────────────────── */}
        <Card variant="outlined" className="cc-panel cc-panel--panel">
          <CardContent className="cc-panel__content">
            <Box className="ae-toolbar">
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, md: 3 }}>
                  <SelectFormField
                    required={false}
                    name="userType"
                    label="User type"
                    value={userType}
                    options={SEND_USER_TYPE_OPTIONS}
                    onChange={(_, v) => { setUserType(String(v)); handleFilterChange(); }}
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <SelectFormField
                    required={false}
                    name="hasOpened"
                    label="Open status"
                    value={hasOpened}
                    options={OPENED_OPTIONS}
                    onChange={(_, v) => { setHasOpened(String(v)); handleFilterChange(); }}
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <SelectFormField
                    required={false}
                    name="hasClicked"
                    label="Click status"
                    value={hasClicked}
                    options={CLICKED_OPTIONS}
                    onChange={(_, v) => { setHasClicked(String(v)); handleFilterChange(); }}
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                  <SelectFormField
                    required={false}
                    name="deliveryStatus"
                    label="Delivery"
                    value={deliveryStatus}
                    options={DELIVERY_FILTER_OPTIONS}
                    onChange={(_, v) => { setDeliveryStatus(String(v)); handleFilterChange(); }}
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormField
                    required={false}
                    name="templateKey"
                    label="Template filter"
                    value={templateKey}
                    onChange={(_, v) => { setTemplateKey(String(v)); handleFilterChange(); }}
                    placeholder="e.g. js-welcome"
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 5 }}>
                  <FormField
                    required={false}
                    name="search"
                    label="Search email / user ID"
                    value={search}
                    onChange={(_, v) => { setSearch(String(v)); handleFilterChange(); }}
                    placeholder="name@company.com or userId"
                    size="small"
                  />
                </Grid>
                {/* flex-end, not center: the sibling cells hold labelled fields about twice this
                    button's height, so centring floated Reset above their input line. */}
                <Grid size={{ xs: 12, md: 3 }} className="ea-toolbar-cell">
                  <Stack direction="row" spacing={1} className="ea-toolbar-actions">
                    <Button
                      variant="text"
                      onClick={resetFilters}
                      disabled={!hasActiveFilters}
                      size="small"
                      sx={{ p: 0, minWidth: 0, textDecoration: 'underline', textTransform: 'none', fontWeight: 400, '&:hover': { background: 'none', textDecoration: 'underline' } }}
                    >
                      Reset
                    </Button>
                    {hasActiveFilters && (
                      <Chip size="small" label="Filters active" color="primary" variant="outlined" />
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Box>

            {/* ── Table ─────────────────────────────────────────────── */}
            <Box className="ae-table-section">
              <PageStats
                total={data?.totalElements ?? 0}
                filtered={data?.content?.length ?? 0}
                label="records"
              />

              {error && (
                <ErrorMessage message={error} />
              )}

              {loading ? (
                <SkeletonLoader rows={10} columns={selectors.length} />
              ) : (
                <MintlyTable
                  rows={data?.content ?? []}
                  selectors={selectors}
                  tableHeadRow={tableHeadRow}
                  tableBodyRow={tableBodyRow}
                  onRowClick={(r: EmailSendRecordItem) => setSelected(r)}
                  pagination={{
                    enabled: true,
                    page,
                    rowsPerPage: PAGE_SIZE,
                    totalNumberOfRows: data?.totalElements ?? 0,
                    handleChangePage: (_: unknown, newPage: number) => setPage(newPage as number),
                  }}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* ── Detail drawer ───────────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        slotProps={{ paper: { sx: { width: { xs: '100%', sm: 440 }, p: 0 } } }}
      >
        {selected && (
          <Box className="ea-drawer">
            <Box className="ea-drawer-header">
              <Box>
                <Typography variant="overline" className="ae-inspector-label">
                  Send record
                </Typography>
                <Typography variant="h6" className="ae-inspector-title">
                  {selected.userEmail || selected.userId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selected.templateKey}{selected.step != null ? ` · step ${selected.step}` : ''}
                </Typography>
              </Box>
              <IconButton onClick={() => setSelected(null)} size="small">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Divider />

            <Box className="ea-drawer-body">
              {/* Summary metrics */}
              <Box className="ea-detail-grid">
                <Box className="ea-detail-item">
                  <Typography variant="caption" className="ae-inspector-metric-label">Sent at</Typography>
                  <Typography variant="body2">{fmtDate(selected.sentAt)}</Typography>
                </Box>
                <Box className="ea-detail-item">
                  <Typography variant="caption" className="ae-inspector-metric-label">User type</Typography>
                  <Typography variant="body2">{emailAudienceLabel(selected.userType)}</Typography>
                </Box>
                <Box className="ea-detail-item">
                  <Typography variant="caption" className="ae-inspector-metric-label">Subject</Typography>
                  <Typography variant="body2">{selected.subject || '-'}</Typography>
                </Box>
                {selected.enrollmentId && (
                  <Box className="ea-detail-item">
                    <Typography variant="caption" className="ae-inspector-metric-label">Enrollment</Typography>
                    <Typography variant="body2" className="ea-mono">{selected.enrollmentId}</Typography>
                  </Box>
                )}
              </Box>

              <Divider />

              {/* Open events */}
              <Box className="ea-drawer-section">
                <Stack direction="row" spacing={1} className="ea-drawer-section-header" sx={{ alignItems: 'center' }}>
                  <OpenInBrowserOutlinedIcon fontSize="small" color={selected.openCount > 0 ? 'success' : 'disabled'} />
                  <Typography variant="subtitle2">
                    Opens: {selected.openCount === 0 ? 'never opened' : `${selected.openCount} time${selected.openCount > 1 ? 's' : ''}`}
                  </Typography>
                </Stack>
                {selected.openCount > 0 && (
                  <Box className="ea-event-list">
                    <Box className="ea-event-row">
                      <Typography variant="caption" className="ae-inspector-metric-label">First open</Typography>
                      <Typography variant="body2">{fmtDate(selected.firstOpenAt)}</Typography>
                    </Box>
                    {selected.lastOpenAt && selected.lastOpenAt !== selected.firstOpenAt && (
                      <Box className="ea-event-row">
                        <Typography variant="caption" className="ae-inspector-metric-label">Last open</Typography>
                        <Typography variant="body2">{fmtDate(selected.lastOpenAt)}</Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              <Divider />

              {/* Click events */}
              <Box className="ea-drawer-section">
                <Stack direction="row" spacing={1} className="ea-drawer-section-header" sx={{ alignItems: 'center' }}>
                  <MouseOutlinedIcon fontSize="small" color={selected.clickCount > 0 ? 'primary' : 'disabled'} />
                  <Typography variant="subtitle2">
                    Clicks: {selected.clickCount === 0 ? 'no clicks' : `${selected.clickCount} click${selected.clickCount > 1 ? 's' : ''}`}
                  </Typography>
                </Stack>
                {selected.clicks && selected.clicks.length > 0 ? (
                  <Box className="ea-click-list">
                    {selected.clicks.map((c, i) => (
                      <Box key={i} className="ea-click-item">
                        <Box className="ea-click-row">
                          <Typography variant="caption" className="ae-inspector-metric-label">URL</Typography>
                          <Typography variant="body2" className="ea-click-url">{c.url || '-'}</Typography>
                        </Box>
                        <Box className="ea-click-row">
                          <Typography variant="caption" className="ae-inspector-metric-label">Time</Typography>
                          <Typography variant="body2">{fmtDate(c.clickedAt)}</Typography>
                        </Box>
                        {c.ipAddress && (
                          <Box className="ea-click-row">
                            <Typography variant="caption" className="ae-inspector-metric-label">IP</Typography>
                            <Typography variant="body2" className="ea-mono">{c.ipAddress}</Typography>
                          </Box>
                        )}
                        {c.userAgent && (
                          <Box className="ea-click-row">
                            <Typography variant="caption" className="ae-inspector-metric-label">User agent</Typography>
                            <Typography variant="caption" className="ea-user-agent">{c.userAgent}</Typography>
                          </Box>
                        )}
                        {i < selected.clicks.length - 1 && <Divider sx={{ my: 0.75 }} />}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    No click events recorded.
                  </Typography>
                )}
              </Box>

              {deliveryChip(selected) && (
                <>
                  <Divider />
                  <Box className="ea-drawer-section">
                    <Stack direction="row" spacing={1} className="ea-drawer-section-header" sx={{ alignItems: 'center' }}>
                      <DeliveryChip label={deliveryChip(selected)!.label} tone={deliveryChip(selected)!.tone} />
                    </Stack>
                    <Box className="ea-event-list">
                      <Box className="ea-event-row">
                        <Typography variant="caption" className="ae-inspector-metric-label">Reported</Typography>
                        <Typography variant="body2">{fmtDate(selected.bouncedAt)}</Typography>
                      </Box>
                      <Box className="ea-event-row">
                        <Typography variant="caption" className="ae-inspector-metric-label">Reason</Typography>
                        <Typography variant="body2">{selected.bounceReason || '-'}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}

              <Divider />

              {/* Tracking ID */}
              <Box className="ea-drawer-section ea-drawer-section--last">
                <Typography variant="caption" className="ae-inspector-metric-label">Tracking ID</Typography>
                <Typography variant="body2" className="ea-mono ea-tracking-id">{selected.id}</Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default EmailAudit;
